import { useCallback, useState } from "react";

export type UseControlledFieldParams<T> = {
  /** When defined, the field is controlled and this value always wins. Flipping
   *  back to `undefined` reverts to uncontrolled with the stale `defaultValue` —
   *  keep it controlled to clear (e.g. `""`), never pass `undefined`. */
  value?: T;
  /** Initial value when uncontrolled. */
  defaultValue?: T;
  onChange?: (next: T) => void;
};

export type UseControlledFieldResult<T> = {
  value: T | undefined;
  setValue: (next: T) => void;
  isControlled: boolean;
};

/**
 * Controlled/uncontrolled value plumbing for form elements: a controlled `value`
 * wins; otherwise the hook owns internal state seeded from `defaultValue`.
 * `setValue` always fires `onChange`, mutating internal state only when uncontrolled.
 */
export function useControlledField<T>({
  value,
  defaultValue,
  onChange,
}: UseControlledFieldParams<T>): UseControlledFieldResult<T> {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<T | undefined>(
    defaultValue,
  );

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return {
    value: isControlled ? value : internalValue,
    setValue,
    isControlled,
  };
}
